import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { teamAPI, coachAPI, organizationAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './ManageOrgTeams.css';
import '../styles/modals.css';

function ManageOrgTeams() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [orgAcronym, setOrgAcronym] = useState('');
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [ageGroupFilter, setAgeGroupFilter] = useState('all');
  const [sportFilter, setSportFilter] = useState('all');
  const [coachStatusFilter, setCoachStatusFilter] = useState('all');
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [coachEmail, setCoachEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [toastMessage, setToastMessage] = useState({ type: '', text: '' });
  // Helper function to show toast messages
  const showToast = (type, text, duration = 3000) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage({ type: '', text: '' }), duration);
  };
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [coachToRemove, setCoachToRemove] = useState(null);
  const fetchTeamsAndCoaches = async () => {
    try {
      // Fetch org acronym
      const orgResponse = await organizationAPI.getDefaults();
      setOrgAcronym(orgResponse.data.orgAcronym || 'Organization');

      // Fetch teams
      const teamsResponse = await teamAPI.getAll();
      // Get org teams (whether you're a coach or not doesn't matter - you own them)
      // Filter out soft-deleted teams (deletedAt !== null)
      const ownedTeams = (
        teamsResponse.data.teams || teamsResponse.data
      ).filter(
        (t) =>
          t.organizationTeam === true &&
          t.deletedAt === null &&
          t.userId === user.id
      );

      // Fetch coaches for each team
      const teamsWithCoaches = await Promise.all(
        ownedTeams.map(async (team) => {
          try {
            const coachesResponse = await coachAPI.getTeamCoaches(team.id);
            return {
              ...team,
              coaches: coachesResponse.data.coaches || [],
            };
          } catch (err) {
            return {
              ...team,
              coaches: [],
            };
          }
        })
      );

      setTeams(teamsWithCoaches);
      setError('');
    } catch (err) {
      if (err.response?.status === 403) {
        showToast(
          'error',
          'Organization subscription required to manage teams. Redirecting...'
        );
        setTimeout(() => navigate('/membership'), 2000);
      } else {
        setError('Failed to load teams');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchTeamsAndCoaches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInviteClick = (team) => {
    setSelectedTeam(team);
    setCoachEmail('');
    setInviteModalOpen(true);
    setError('');
    setSuccessMessage('');
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    setInviting(true);
    setError('');
    setSuccessMessage('');

    try {
      // Check if org admin is inviting themselves
      const isSelfAssignment =
        user?.email.toLowerCase() === coachEmail.toLowerCase();

      if (isSelfAssignment) {
        // Auto-assign without sending email
        await coachAPI.assignSelfAsCoach(selectedTeam.id);
        setSuccessMessage(`✅ You've been assigned as coach!`);
      } else {
        // Normal invite flow - send email
        await coachAPI.inviteCoach(selectedTeam.id, coachEmail);
        setSuccessMessage(`✅ Invitation sent to ${coachEmail}!`);
      }

      setCoachEmail('');
      setTimeout(() => {
        setInviteModalOpen(false);
        setSuccessMessage('');
        fetchTeamsAndCoaches(); // Refresh to show the new coach
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign coach');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveCoach = (teamId, coachId, coachName, isPending) => {
    setCoachToRemove({ teamId, coachId, coachName, isPending });
    setShowRemoveModal(true);
  };

  const confirmRemoveCoach = async () => {
    if (!coachToRemove) return;

    const { teamId, coachId, coachName, isPending } = coachToRemove;

    try {
      await coachAPI.removeCoach(teamId, coachId);
      const message = isPending
        ? `Invitation cancelled for ${coachName}`
        : `${coachName} removed successfully`;

      showToast('success', message);
      fetchTeamsAndCoaches();
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Failed to remove coach');
    } finally {
      setShowRemoveModal(false);
      setCoachToRemove(null);
    }
  };

  const handleResendInvite = async (teamId, userId, email) => {
    try {
      await coachAPI.resendInvite(teamId, userId);
      setSuccessMessage(`✅ Invitation resent to ${email}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend invitation');
    }
  };

  const filteredTeams = teams.filter((team) => {
    // Search filter
    const matchesSearch =
      team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      team.ageGroup?.toLowerCase().includes(searchQuery.toLowerCase());

    // Age group filter
    const matchesAgeGroup =
      ageGroupFilter === 'all' || team.ageGroup === ageGroupFilter;

    // Sport filter
    const matchesSport = sportFilter === 'all' || team.sport === sportFilter;

    // Coach status filter
    const matchesCoachStatus =
      coachStatusFilter === 'all' ||
      (coachStatusFilter === 'assigned' && team.coaches.length > 0) ||
      (coachStatusFilter === 'unassigned' && team.coaches.length === 0);

    return (
      matchesSearch && matchesAgeGroup && matchesSport && matchesCoachStatus
    );
  });

  // Get unique age groups for filter dropdown
  const uniqueAgeGroups = [
    ...new Set(teams.map((t) => t.ageGroup).filter(Boolean)),
  ].sort();

  // Get unique sports for filter dropdown
  const uniqueSports = [
    ...new Set(teams.map((t) => t.sport).filter(Boolean)),
  ].sort();

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="manage-org-teams-container">
          <div className="loading">Loading teams...</div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="manage-org-teams-container">
        <div className="manage-org-teams-content">
          <div className="form-header">
            <button onClick={() => navigate('/')} className="back-button">
              ← Back to Home
            </button>
            <h1>Manage {orgAcronym} Teams</h1>
            <p>Manage teams and coaches for your organization</p>
          </div>

          {error && <div className="error-message">{error}</div>}
          {successMessage && (
            <div className="success-message">{successMessage}</div>
          )}

          <div className="filters-section">
            <div className="search-and-filters">
              <input
                type="text"
                placeholder="🔍 Search teams..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />

              <div className="filters-row">
                <div className="filter-group">
                  <label>Age Group</label>
                  <select
                    value={ageGroupFilter}
                    onChange={(e) => setAgeGroupFilter(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Age Groups</option>
                    {uniqueAgeGroups.map((age) => (
                      <option key={age} value={age}>
                        {age}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label>Sport</label>
                  <select
                    value={sportFilter}
                    onChange={(e) => setSportFilter(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Sports</option>
                    {uniqueSports.map((sport) => (
                      <option key={sport} value={sport}>
                        {sport === 'Baseball' ? '⚾ Baseball' : '🥎 Softball'}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label>Coach Status</label>
                  <select
                    value={coachStatusFilter}
                    onChange={(e) => setCoachStatusFilter(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Teams</option>
                    <option value="assigned">Has Coach</option>
                    <option value="unassigned">No Coach</option>
                  </select>
                </div>

                {(ageGroupFilter !== 'all' ||
                  sportFilter !== 'all' ||
                  coachStatusFilter !== 'all' ||
                  searchQuery !== '') && (
                  <button
                    className="btn-clear-filters"
                    onClick={() => {
                      setAgeGroupFilter('all');
                      setSportFilter('all');
                      setCoachStatusFilter('all');
                      setSearchQuery('');
                    }}
                  >
                    ✕ Clear Filters
                  </button>
                )}
              </div>
            </div>

            <div className="results-summary">
              <span className="team-count">
                {filteredTeams.length} of {teams.length}{' '}
                {filteredTeams.length === 1 ? 'team' : 'teams'}
              </span>
            </div>
          </div>

          {filteredTeams.length === 0 ? (
            <div className="empty-state">
              <h3>No teams found</h3>
              <p>Create teams first to invite coaches</p>
              <button
                className="btn btn-primary"
                onClick={() => navigate('/create-team')}
              >
                Create Team
              </button>
            </div>
          ) : (
            <div className="teams-list">
              {filteredTeams.map((team) => (
                <div key={team.id} className="team-card-manage">
                  <div className="team-header-manage">
                    <div className="team-info-manage">
                      {team.logoUrl && (
                        <img
                          src={team.logoUrl}
                          alt={team.name}
                          className="team-logo-small"
                        />
                      )}
                      <div>
                        <h3>{team.name}</h3>
                        <p className="team-meta">
                          {team.ageGroup} •{' '}
                          {team.sport === 'Softball' ? '🥎' : '⚾'} {team.sport}{' '}
                          • {team.season}
                        </p>
                      </div>
                    </div>
                    <div className="team-colors-manage">
                      <span
                        className="color-dot-large"
                        style={{ backgroundColor: team.primaryColor }}
                        title={team.primaryColor}
                      />
                      <span
                        className="color-dot-large"
                        style={{ backgroundColor: team.secondaryColor }}
                        title={team.secondaryColor}
                      />
                    </div>
                  </div>

                  <div className="team-actions">
                    <button
                      className="btn-edit-team"
                      onClick={() => navigate(`/edit-team/${team.id}`)}
                    >
                      ✏️ Edit Team
                    </button>
                  </div>

                  <div className="coaches-section">
                    <div className="coaches-header">
                      <h4>Assigned Coaches ({team.coaches.length})</h4>
                      <button
                        className="btn-invite"
                        onClick={() => handleInviteClick(team)}
                      >
                        + Invite Coach
                      </button>
                    </div>

                    {team.coaches.length > 0 ? (
                      <div className="coaches-list">
                        {team.coaches.map((coach) => (
                          <div
                            key={coach.id}
                            className={`coach-item ${
                              coach.isPending ? 'pending' : ''
                            }`}
                          >
                            <div className="coach-info">
                              <span className="coach-icon">
                                {coach.isPending ? '📧' : '👤'}
                              </span>
                              <div>
                                <p className="coach-name">
                                  {coach.isPending
                                    ? coach.email
                                    : `${coach.firstName} ${coach.lastName}`}
                                </p>
                                <p
                                  className={`coach-email ${
                                    coach.isPending ? 'status-pending' : ''
                                  }`}
                                >
                                  {coach.isPending
                                    ? 'Status: Invitation Pending'
                                    : coach.email}
                                </p>
                              </div>
                            </div>
                            <div className="coach-actions">
                              {coach.isPending ? (
                                <>
                                  <button
                                    className="btn-resend"
                                    onClick={() =>
                                      handleResendInvite(
                                        team.id,
                                        coach.id,
                                        coach.email
                                      )
                                    }
                                  >
                                    Resend
                                  </button>
                                  <button
                                    className="btn-remove"
                                    onClick={() =>
                                      handleRemoveCoach(
                                        team.id,
                                        coach.id,
                                        coach.email,
                                        true
                                      )
                                    }
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <button
                                  className="btn-remove"
                                  onClick={() =>
                                    handleRemoveCoach(
                                      team.id,
                                      coach.id,
                                      `${coach.firstName} ${coach.lastName}`,
                                      false
                                    )
                                  }
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-coaches">No coaches assigned yet</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Invite Modal */}
      {inviteModalOpen && (
        <div
          className="modal-overlay"
          onClick={() => setInviteModalOpen(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Invite Coach to {selectedTeam?.name}</h2>
              <button
                className="modal-close"
                onClick={() => setInviteModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleInviteSubmit}>
              <div className="form-group">
                <label>Coach Email Address</label>
                <input
                  type="email"
                  value={coachEmail}
                  onChange={(e) => setCoachEmail(e.target.value)}
                  placeholder="coach@example.com"
                  required
                  autoFocus
                />
                <p className="field-hint">
                  They'll receive an email invitation to join this team
                </p>
              </div>

              {error && <div className="error-message">{error}</div>}
              {successMessage && (
                <div className="success-message">{successMessage}</div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setInviteModalOpen(false)}
                  disabled={inviting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={inviting}
                >
                  {inviting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Remove Coach Confirmation Modal */}
      {showRemoveModal && coachToRemove && (
        <div
          className="modal-overlay"
          onClick={() => setShowRemoveModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>
              {coachToRemove.isPending ? 'Cancel Invitation?' : 'Remove Coach?'}
            </h3>
            <p>
              Are you sure you want to{' '}
              {coachToRemove.isPending ? 'cancel the invitation for' : 'remove'}{' '}
              <strong>{coachToRemove.coachName}</strong>?
            </p>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setShowRemoveModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-confirm-delete"
                onClick={confirmRemoveCoach}
              >
                {coachToRemove.isPending ? 'Cancel Invitation' : 'Remove Coach'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      {toastMessage.text && toastMessage.type === 'success' && (
        <div className="toast-message toast-success">{toastMessage.text}</div>
      )}
      {toastMessage.text && toastMessage.type === 'error' && (
        <div className="toast-message toast-error">{toastMessage.text}</div>
      )}

      <Footer />
    </>
  );
}

export default ManageOrgTeams;
